/**
 * Conflict Summary Panel Component
 *
 * Always-visible panel displaying conflict detection status.
 * - Shows positive "no conflicts" state when no conflicts exist (gray, non-interactive)
 * - Shows collapsible conflict list when conflicts are detected (neutral gray styling, interactive)
 * - Displays both conflicting tasks: operator name, then task1 vs task2 with times and names
 * - Clicking on a conflict scrolls to the conflict location in the timeline
 * - Auto-selects the corresponding service and operator when clicking a conflict
 *
 * UX improvement: Panel is always visible to provide consistent feedback,
 * whether conflicts exist or not, following empty state best practices.
 * Uses neutral gray styling with orange icon for visual distinction.
 * UPDATED: Conflict click now passes serviceId directly for immediate service selection
 * UPDATED: Shows both conflicting tasks with their respective times and names
 * UPDATED: Added defaultCollapsed prop for mobile optimization - starts collapsed to save space
 */

import { useState } from 'react';
import { AlertTriangle, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { TaskConflict } from '../../lib/conflictDetection';

interface ConflictSummaryPanelProps {
  conflicts: TaskConflict[];
  loading: boolean;
  onConflictClick?: (userId: string, time: string, serviceId1: string | null, serviceId2: string | null) => void;
  defaultCollapsed?: boolean; // If true, starts collapsed even if conflicts exist
}

export function ConflictSummaryPanel({
  conflicts,
  loading,
  onConflictClick,
  defaultCollapsed = false
}: ConflictSummaryPanelProps) {
  // Filter only active conflicts (non-ignored)
  const activeConflicts = conflicts.filter(c => !c.isIgnored);

  // Start collapsed if no conflicts, expanded if conflicts exist (unless defaultCollapsed is true)
  const [isExpanded, setIsExpanded] = useState(defaultCollapsed ? false : activeConflicts.length > 0);

  const formatTime = (time: string) => {
    return time.substring(0, 5);
  };

  const handleConflictClick = (conflict: TaskConflict) => {
    if (onConflictClick) {
      onConflictClick(conflict.userId, conflict.task1.scheduledTime, conflict.task1.serviceId, conflict.task2.serviceId);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  const hasConflicts = activeConflicts.length > 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {hasConflicts ? (
        <>
          {/* Collapsible Header - Only when conflicts exist */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full px-4 py-3 flex items-center justify-between transition-colors hover:bg-gray-50"
          >
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-orange-600 mr-2" />
              <span className="text-sm font-medium text-gray-900">
                Conflits ({activeConflicts.length})
              </span>
            </div>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>

          {/* Conflict List */}
          {isExpanded && (
            <div className="border-t border-gray-200">
              {activeConflicts.map((conflict, index) => (
                <button
                  key={`${conflict.userId}-${conflict.task1.id}-${index}`}
                  onClick={() => handleConflictClick(conflict)}
                  className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                >
                  <div className="font-medium text-gray-900 mb-1">{conflict.userName}</div>
                  <div className="flex items-center text-xs">
                    <span className="text-gray-600">{formatTime(conflict.task1.scheduledTime)}</span>
                    <span className="mx-1.5 text-gray-400">•</span>
                    <span className="text-gray-700">{conflict.task1.taskName}</span>
                    <span className="mx-2 text-orange-500 font-medium">vs</span>
                    <span className="text-gray-600">{formatTime(conflict.task2.scheduledTime)}</span>
                    <span className="mx-1.5 text-gray-400">•</span>
                    <span className="text-gray-700">{conflict.task2.taskName}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        /* Non-interactive header - When no conflicts */
        <div className="px-4 py-3 flex items-center">
          <CheckCircle className="w-5 h-5 text-gray-400 mr-2" />
          <span className="text-sm font-medium text-gray-600">
            Aucun conflit
          </span>
        </div>
      )}
    </div>
  );
}
